import { getLogger } from '@blueberry/observability'
import { Application } from 'stimulus'
import { registerControllers as registerControllersHelper } from 'stimulus-vite-helpers'
import { commitSha, dev, environment } from './config'

const application = Application.start()
const logger = getLogger('setup_stimulus')

/**
 * @example
 * ```
 * import { setupStimulus } from '@blueberry/framework'
 *
 * setupStimulus({
 *   ...import.meta.glob('./controllers/*_controller.ts', { eager: true }),
 *   'hello': () => import('../../lib/hello_controller'),
 * })
 * ```
 */
export const setupStimulus = (controllers: Record<string, unknown>) => {
  registerControllersHelper(application, controllers)

  if (dev) {
    // Provides logs for each lifecycle event
    // and action taken on a controller.
    //
    // @example
    // ```
    // application#starting
    // messages-main#connect
    // ...
    // (user clicks on the toggle button)
    // messages-main#toggleIsCollapsed
    // ```
    application.debug = true
    registerDevTools()
  }

  attachCommitShaToFrameRequests()

  return application
}

export const registerControllers = (controllers: Record<string, unknown>) => {
  registerControllersHelper(application, controllers)
}

const attachCommitShaToFrameRequests = () => {
  window.addEventListener('turbo:before-fetch-request', (event) => {
    try {
      const request = (event as any)?.detail?.fetchOptions
      if (!request) {
        return
      }

      if (!request.headers) {
        request.headers = new Headers()
      }

      request.headers['X-Commit-Sha'] = commitSha
    } catch (e) {
      logger.error('Failed to attach headers to turbo request', e)
    }
  })
}

const registerDevTools = () => {
  /**
   * Errors from <turbo-frame>'s will be consumed and only visible
   * on the network tab by default.
   *
   * This listens for errors and renders them in a modal.
   */
  document.addEventListener('turbo:frame-missing', async function (e) {
    const response: Response = (e as any).detail.response
    const content_type = response.headers.get('Content-Type')

    if (content_type?.includes('text/html')) {
      const html = await response.text()
      const iframe = document.createElement('iframe')
      iframe.srcdoc = html
      iframe.style.width = '100%'
      iframe.style.height = '100%'

      const modal = document.createElement('div')
      modal.style.position = 'fixed'
      modal.style.top = '5vh'
      modal.style.left = '20vw'
      modal.style.width = '60vw'
      modal.style.height = '90vh'

      modal.appendChild(iframe)
      window.document.body.appendChild(modal)
    }
  })
}

const assertDebug = (condition: boolean, msg: string) => {
  if (!condition) {
    if (environment != 'production') {
      throw new Error(msg)
    } else {
      logger.error(msg)
    }
  }
}

// On turbo:before-stream-render (https://turbo.hotwired.dev/reference/events#turbo%3Abefore-stream-render)
// modify the stream element before it is rendered, if the Turbo Stream Action is 'replace', otherwise render as normal.
// This allows us to merge attributes from the original frame into the new frame that would otherwise be lost when
// Turbo replaces the original "outer" frame with the new "inner" frame.
addEventListener('turbo:before-stream-render', (event) => {
  const renderTurboStream = event.detail.render

  event.detail.render = function (streamElement: HTMLElement) {
    if (streamElement.action !== 'replace') {
      renderTurboStream(streamElement)
      return
    }

    assertDebug(
      streamElement.children.length == 1,
      `Turbo Stream element should have only one child. Got ${
        streamElement.children.length
      } (${streamElement.getAttribute('target')})`,
    )
    assertDebug(
      streamElement.children[0].tagName === 'TEMPLATE',
      'Turbo Stream element should have a template child',
    )

    const template = streamElement.children[0]
    assertDebug(
      template.content.children.length == 1,
      `Turbo Stream template should have only one child. Got ${
        template.children.length
      } (${streamElement.getAttribute('target')})`,
    )

    const newFrame: HTMLElement = template.content.children[0]
    assertDebug(
      newFrame.tagName === 'TURBO-FRAME',
      'Template should have a turbo-frame child',
    )

    const originalFrame = document.getElementById(newFrame.id)
    if (!originalFrame) {
      logger.error(
        'Failed to find original frame for turbo stream element',
        streamElement,
      )
      alert(
        'Page update resulted in an error. Please refresh the page to try again.',
      )
      return
    }

    Array.from(originalFrame.attributes)
      .filter((a) => a.name !== 'src' && a.name !== 'lazy-src') // exclude src, since we're updating an existing frame
      .filter((a) => !streamElement.hasAttribute(a.name)) // exclude existing attributes, so we don't overwrite them
      .forEach((a) => {
        newFrame.setAttribute(a.name, a.value) // set any unset attributes in the new frame with attributes from the original frame
      })
    renderTurboStream(streamElement)
  }
})
